import type { ProgrammeState } from '@repwise/domain';
import { useCallback, useEffect, useState } from 'react';
import { gateState, type GateState } from './gate.ts';
import { useRepo } from './hooks.ts';

/** Collects the gate inputs from the store, engine and auth, and drives the restore pull (ADR-0016). */
export function useGate(programme: ProgrammeState | null): { gate: GateState; retryRestore: () => void } {
  const repo = useRepo();
  const { store, engine } = repo;
  const [tick, setTick] = useState(0);
  const [inputs, setInputs] = useState<{ profileClaimed: boolean; signedOut: boolean; needsClaim: boolean; pulledOnce: boolean } | null>(null);
  const [restoreFailed, setRestoreFailed] = useState(false);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const offStore = store.onChange(bump);
    const offAuth = repo.onAuth(bump);
    return () => {
      offStore();
      offAuth();
    };
  }, [store, repo, bump]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const signedIn = !!repo.user();
      const bound = signedIn ? await engine.bindUser() : 'signed_out';
      const profile = await store.activeProfile();
      const next = {
        profileClaimed: !!profile.claimedBy,
        signedOut: (await store.meta<boolean>('signedOut')) === true,
        needsClaim: bound === 'needs_claim',
        pulledOnce: signedIn ? await engine.hasPulledOnce() : false,
      };
      if (alive) setInputs(next);
    })().catch(() => undefined); // the store was wiped and closed; the app is about to reload
    return () => {
      alive = false;
    };
  }, [tick, repo, engine, store]);

  const gate = gateState({
    authConfigured: !!repo.auth,
    ready: !!inputs && !!programme,
    signedIn: !!repo.user(),
    profileClaimed: inputs?.profileClaimed ?? false,
    signedOut: inputs?.signedOut ?? false,
    needsClaim: inputs?.needsClaim ?? false,
    pulledOnce: inputs?.pulledOnce ?? false,
    restoreFailed,
    programmeStarted: programme?.started ?? false,
  });

  // Restoring: pull once; failure (offline/auth) shows a retry, never onboarding.
  useEffect(() => {
    if (gate.kind !== 'restoring') return;
    let alive = true;
    void engine.drain().then(async (r) => {
      if (!alive) return;
      if (r.skipped === 'offline' || r.skipped === 'auth') setRestoreFailed(true);
      else if (r.skipped === 'busy') setTimeout(bump, 500);
      else if (!(await engine.hasPulledOnce())) setRestoreFailed(true);
      bump();
    });
    return () => {
      alive = false;
    };
  }, [gate.kind, engine, bump]);

  return {
    gate,
    retryRestore: () => {
      setRestoreFailed(false);
      bump();
    },
  };
}
